import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import useSWR from 'swr';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Layout, LoadingSpinner, ErrorMessage } from '@/shared/components';
import { useNotificationActions } from '@/shared/stores/notification-store';
import { doctorsApi } from '@/shared/api';
import { DraggableDoctorRow } from '@/features/doctors/DraggableDoctorRow';
import type { DoctorWithServices } from '@/shared/types';

/**
 * Страница управления врачами
 * Отображает список всех врачей с возможностью поиска, фильтрации и управления
 */
export const DoctorsPage: React.FC = () => {
  const [search, setSearch] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [localDoctors, setLocalDoctors] = useState<DoctorWithServices[]>([]);
  const [isUpdatingSortOrder, setIsUpdatingSortOrder] = useState(false);

  // Хуки для уведомлений
  const { showSuccess, showError } = useNotificationActions();

  // Загрузка списка врачей
  const {
    data: doctorsResponse,
    error,
    isLoading,
    mutate
  } = useSWR(
    ['doctors', search, includeInactive],
    () =>
      doctorsApi.getAll({
        ...(search && { search }),
        includeInactive
      }),
    {
      refreshInterval: 30000
    }
  );

  const doctors = doctorsResponse?.doctors || [];

  // Синхронизация локального состояния с данными из API
  React.useEffect(() => {
    if (doctors.length > 0) {
      setLocalDoctors(doctors);
    }
  }, [doctors]);

  /**
   * Обработчик изменения статуса врача
   */
  const handleStatusChange = async (
    doctor: DoctorWithServices
  ): Promise<void> => {
    try {
      await doctorsApi.updateStatus(doctor.id, !doctor.isActive);
      await mutate();
      showSuccess(
        `Статус врача "${doctor.fullName}" успешно ${doctor.isActive ? 'деактивирован' : 'активирован'}`
      );
    } catch (error) {
      console.error('Error updating doctor status:', error);
      showError('Ошибка при изменении статуса врача');
    }
  };

  /**
   * Обработчик перемещения врача в списке
   */
  const handleMoveDoctor = useCallback(
    (dragIndex: number, hoverIndex: number): void => {
      const draggedDoctor = localDoctors[dragIndex];
      if (!draggedDoctor) return;

      const newDoctors = [...localDoctors];

      // Удаляем элемент из старой позиции
      newDoctors.splice(dragIndex, 1);
      // Вставляем элемент в новую позицию
      newDoctors.splice(hoverIndex, 0, draggedDoctor);

      setLocalDoctors(newDoctors);
    },
    [localDoctors]
  );

  /**
   * Обработчик завершения перетаскивания - обновление sortOrder на сервере
   */
  const handleDragEnd = useCallback(async (): Promise<void> => {
    if (isUpdatingSortOrder) return;

    setIsUpdatingSortOrder(true);

    try {
      // Создаем массив обновлений с новыми sortOrder
      const updates = localDoctors.map((doctor, index) => ({
        id: doctor.id,
        sortOrder: index + 1 // sortOrder начинается с 1
      }));

      // Отправляем обновления на сервер
      await doctorsApi.updateSortOrder(updates);

      // Обновляем данные из API
      await mutate();

      showSuccess('Порядок врачей успешно обновлен');
    } catch (error) {
      console.error('Error updating doctors sort order:', error);
      // Возвращаем исходный порядок при ошибке
      setLocalDoctors(doctors);
      showError('Ошибка при обновлении порядка врачей');
    } finally {
      setIsUpdatingSortOrder(false);
    }
  }, [localDoctors, doctors, mutate, isUpdatingSortOrder]);

  return (
    <DndProvider backend={HTML5Backend}>
      <Layout title="Управление врачами">
        <div className="flex flex-col gap-2">
          {/* Заголовок и кнопка добавления */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h2 className="text-xl font-semibold text-base-content">
                Список врачей
              </h2>
              <p className="text-base-content/60 mt-1">
                Управление информацией о врачах клиники
              </p>
            </div>
            <Link to="/doctors/create" className="btn btn-primary">
              <i className="fas fa-plus"></i>
              Добавить врача
            </Link>
          </div>

          {/* Фильтры и поиск */}
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body p-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <div className="form-control">
                    <div className="input-group flex gap-2">
                      <input
                        type="text"
                        placeholder="Поиск по имени или специализации..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="input input-bordered w-full"
                      />
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="includeInactive"
                    checked={includeInactive}
                    onChange={(e) => setIncludeInactive(e.target.checked)}
                    className="checkbox checkbox-primary"
                  />
                  <label
                    htmlFor="includeInactive"
                    className="text-sm font-medium"
                  >
                    Показать неактивных
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Список врачей */}
          <div className="card card-bordered bg-base-100 shadow-sm">
            <div className="card-body p-2">
              {isLoading && (
                <div className="flex justify-center py-8">
                  <LoadingSpinner size="lg" text="Загрузка врачей..." />
                </div>
              )}

              {error && (
                <ErrorMessage message="Ошибка загрузки списка врачей. Попробуйте обновить страницу." />
              )}

              {!isLoading && !error && doctors.length === 0 && (
                <div className="text-center py-8">
                  <i className="fas fa-user-md text-4xl text-base-content/30 mb-4"></i>
                  <p className="text-base-content/60">
                    {search || includeInactive
                      ? 'Врачи не найдены'
                      : 'Пока нет добавленных врачей'}
                  </p>
                </div>
              )}

              {!isLoading && !error && localDoctors.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="table table-zebra w-full">
                    <thead>
                      <tr>
                        <th className="w-8">
                          <i
                            className="fas fa-grip-vertical text-base-content/40"
                            title="Перетащите для изменения порядка"
                          ></i>
                        </th>
                        <th>Фото</th>
                        <th>Имя</th>
                        <th>Специализация</th>
                        <th>Статус</th>
                        <th>Действия</th>
                      </tr>
                    </thead>
                    <tbody>
                      {localDoctors.map((doctor, index) => (
                        <DraggableDoctorRow
                          key={doctor.id}
                          doctor={doctor}
                          index={index}
                          onMove={handleMoveDoctor}
                          onDragEnd={handleDragEnd}
                          onStatusChange={handleStatusChange}
                        />
                      ))}
                    </tbody>
                  </table>

                  {/* Индикатор обновления порядка */}
                  {isUpdatingSortOrder && (
                    <div className="flex items-center justify-center py-4">
                      <div className="flex items-center gap-2 text-primary">
                        <span className="loading loading-spinner loading-sm"></span>
                        <span>Обновление порядка...</span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </Layout>
    </DndProvider>
  );
};
